import { useEffect, useRef, useState, useCallback } from 'react';
import { FaceDetection } from '@mediapipe/face_detection';

/**
 * Custom hook for real-time multiple face detection using MediaPipe
 * Runs in parallel with Innovatrics face capture
 * 
 * @param {Object} options - Configuration options
 * @param {HTMLVideoElement} options.videoElement - Video element from Innovatrics
 * @param {boolean} options.enabled - Whether detection is enabled
 * @param {Function} options.onMultipleFacesDetected - Callback when multiple faces detected
 * @param {Function} options.onSingleFaceDetected - Callback when single face detected
 * @returns {Object} Detection state and controls
 */
function useMediaPipeFaceDetection({
    videoElement,
    enabled = true,
    onMultipleFacesDetected,
    onSingleFaceDetected,
}) {
    const faceDetectionRef = useRef(null);
    const animationFrameRef = useRef(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [faceCount, setFaceCount] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);

    // Initialize MediaPipe Face Detection
    useEffect(() => {
        if (!enabled || !videoElement) {
            return;
        }

        const initializeFaceDetection = async () => {
            try {
                // Initialize MediaPipe Face Detection
                const faceDetection = new FaceDetection({
                    locateFile: (file) => {
                        // Use CDN for MediaPipe files (lightweight and works in webview/iframe)
                        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`;
                    },
                });

                // Set options before initializing
                faceDetection.setOptions({
                    model: 'short', // Use lightweight 'short' model for better performance
                    minDetectionConfidence: 0.5,
                });

                // Register results callback
                faceDetection.onResults((results) => {
                    try {
                        const detectedFaces = results.detections || [];
                        const count = detectedFaces.length;
                        setFaceCount(count);

                        if (count > 1) {
                            onMultipleFacesDetected?.(count, detectedFaces);
                        } else if (count === 1 || count === 0) {
                            // Treat 0 faces as single face scenario (no multiple faces)
                            onSingleFaceDetected?.(count, detectedFaces);
                        }
                    } catch (error) {
                        console.error('[MediaPipe] Error in results callback:', error);
                    }
                });

                // Initialize the solution (loads models)
                await faceDetection.initialize();

                faceDetectionRef.current = faceDetection;
                setIsInitialized(true);
            } catch (error) {
                console.error('[MediaPipe] Error initializing face detection:', error);
                setIsInitialized(false);
            }
        };

        // Wait for video to be ready
        if (videoElement.readyState >= 2) {
            initializeFaceDetection();
        } else {
            const onLoadedMetadata = () => {
                initializeFaceDetection();
                videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
            };
            videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (faceDetectionRef.current) {
                // Close the face detection instance properly
                faceDetectionRef.current.close().catch((error) => {
                    console.error('[MediaPipe] Error closing face detection:', error);
                });
                faceDetectionRef.current = null;
            }
            setIsInitialized(false);
        };
    }, [enabled, videoElement, onMultipleFacesDetected, onSingleFaceDetected]);

    // Process video frames using requestAnimationFrame
    const processFrame = useCallback(() => {
        if (!faceDetectionRef.current || !videoElement || !isInitialized) {
            animationFrameRef.current = requestAnimationFrame(processFrame);
            return;
        }

        // Check if video element is valid and has valid dimensions
        if (!videoElement.videoWidth || !videoElement.videoHeight) {
            animationFrameRef.current = requestAnimationFrame(processFrame);
            return;
        }

        // Check if video is ready
        if (videoElement.readyState < videoElement.HAVE_CURRENT_DATA) {
            animationFrameRef.current = requestAnimationFrame(processFrame);
            return;
        }

        // Skip if already processing to avoid queue buildup
        if (isProcessing) {
            animationFrameRef.current = requestAnimationFrame(processFrame);
            return;
        }

        // Set processing flag and process frame
        setIsProcessing(true);

        // Process frame with MediaPipe using the existing video element
        faceDetectionRef.current.send({ image: videoElement })
            .then(() => {
                setIsProcessing(false);
                // Continue processing frames
                animationFrameRef.current = requestAnimationFrame(processFrame);
            })
            .catch((error) => {
                console.error('[MediaPipe] Error in send promise:', error);
                setIsProcessing(false);
                // Continue processing frames even on error
                animationFrameRef.current = requestAnimationFrame(processFrame);
            });
    }, [videoElement, isInitialized, isProcessing]);

    // Start processing when ready
    useEffect(() => {
        if (enabled && isInitialized && videoElement && videoElement.readyState >= 2) {
            processFrame();
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [enabled, isInitialized, videoElement, processFrame]);

    return {
        faceCount,
        isInitialized,
        isProcessing,
    };
}

export default useMediaPipeFaceDetection;

